import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import type { TravelExpenseEntriesController } from '../hooks/useTravelExpenseEntries'
import { businessCost, carCost, formatEuro, formatOdometer, formatTimeRange } from '../travelExpenseFormat'

/** Sort fields accepted by the company endpoint. */
const SortFields = {
  Date: 'date',
  EmployeeName: 'employeeName',
  DailyAllowance: 'dailyAllowance',
} as const

const COL_COUNT = 14

interface Props {
  entries: TravelExpenseEntriesController
  /** Hidden on the employee page, where the employee is fixed. */
  showEmployee?: boolean
}

/** Read-only list of travel days with the calculated daily allowance (table + pagination). */
export function TravelExpenseEntriesTable({ entries, showEmployee = true }: Props) {
  const { t } = useTranslation(['service-modules-travel-expenses', 'common'])
  const sortProps = { currentSort: entries.sort, currentOrder: entries.order, onSort: entries.onSort }

  return (
    <TableCard>
      <div className="flex-1 overflow-auto scrollbar-custom relative">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
            <TableRow>
              {showEmployee && (
                <SortableTableHead field={SortFields.EmployeeName} {...sortProps}>
                  {t('columns.employee', { ns: 'common' })}
                </SortableTableHead>
              )}
              <SortableTableHead field={SortFields.Date} {...sortProps}>
                {t('table.date')}
              </SortableTableHead>
              <TableHead>{t('table.time')}</TableHead>
              <SortableTableHead field={SortFields.DailyAllowance} {...sortProps} className="text-right">
                {t('table.dailyAllowance')}
              </SortableTableHead>
              <TableHead className="text-right">{t('table.ownContribution')}</TableHead>
              <TableHead className="text-right">{t('table.kilometers')}</TableHead>
              <TableHead className="text-right">{t('table.overnightCost')}</TableHead>
              <TableHead className="text-right">{t('table.hospitalityCost')}</TableHead>
              <TableHead className="text-right">{t('table.businessCost')}</TableHead>
              <TableHead className="text-right">{t('table.carCost')}</TableHead>
              <TableHead className="text-right">{t('table.odometer')}</TableHead>
              <TableHead className="text-right">{t('table.receipts')}</TableHead>
              <TableHead className="text-right">{t('table.total')}</TableHead>
              <TableHead>{t('table.remark')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <LoaderTable
              isLoading={entries.isLoading}
              isError={entries.isError}
              isEmpty={!entries.rows || entries.rows.length === 0}
              colSpan={showEmployee ? COL_COUNT : COL_COUNT - 1}
              errorMessage={t('table.loadError')}
              emptyMessage={t('table.empty')}
            >
              {entries.rows?.map((entry) => (
                <TableRow key={entry.id}>
                  {showEmployee && (
                    <TableCell>
                      {`${entry.employeeFirstName ?? ''} ${entry.employeeLastName ?? ''}`.trim() || '—'}
                    </TableCell>
                  )}
                  <TableCell className="tabular-nums">{dayjs(entry.date).format('DD.MM.YYYY')}</TableCell>
                  <TableCell className="tabular-nums">{formatTimeRange(entry, t('table.fullDay'))}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatEuro(entry.dailyAllowance)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(entry.ownContribution)}</TableCell>
                  <TableCell className="text-right tabular-nums">{entry.kilometers ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(entry.overnightCost)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(entry.hospitalityCost)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(businessCost(entry))}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatEuro(carCost(entry))}</TableCell>
                  <TableCell className="text-right tabular-nums whitespace-nowrap">
                    {formatOdometer(entry.startKilometers, entry.endKilometers)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{entry.receiptCount ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatEuro(entry.total)}</TableCell>
                  <TableCell className="max-w-64 truncate" title={entry.remark ?? undefined}>
                    {entry.remark || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </LoaderTable>
          </TableBody>
        </Table>
      </div>

      <PaginationFooter
        offset={entries.offset}
        limit={entries.limit}
        totalCount={entries.totalCount}
        onOffsetChange={entries.onOffsetChange}
        onPageSizeChange={entries.onPageSizeChange}
      />
    </TableCard>
  )
}
